import React from "react";
import { Box, Button, InputAdornment, MenuItem, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { SentEmail, getSentEmails } from "../../common/requests/emails/endpoints";
import { Banner, Column, DataTable, KpiCard, KpiStrip, StatusPill, useResource } from "../../ui";
import SentEmailDialog from "./SentEmailDialog";
import { daysAgo, formatWhen, isIpBlock, isoDay, percent, statusMeta } from "./emailData";
import BrevoErrorAlert from "./BrevoErrorAlert";

const PAGE_SIZE = 100;

const PERIODS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "custom", label: "Pick dates…" },
];

const STATUS_FILTERS = [
  { value: "all", label: "Any status" },
  { value: "delivered", label: "Delivered, not opened" },
  { value: "opened", label: "Opened" },
  { value: "clicked", label: "Clicked" },
  { value: "problems", label: "Problems (bounced, blocked, spam)" },
  { value: "sent", label: "No delivery report yet" },
];

const emailId = (m: SentEmail) => `${m.message_id}|${m.email}`;

const columns: Column<SentEmail>[] = [
  {
    key: "subject",
    header: "Subject",
    card: "title",
    minWidth: 200,
    sortValue: (m) => (m.subject || "").toLowerCase(),
    render: (m) => (
      <Box component="span" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
        {m.subject || "(no subject)"}
      </Box>
    ),
  },
  {
    key: "to",
    header: "To",
    card: "subtitle",
    sortValue: (m) => m.email.toLowerCase(),
    render: (m) => <Box component="span" sx={{ overflowWrap: "anywhere" }}>{m.email}</Box>,
  },
  {
    key: "template",
    header: "Email type",
    card: "hidden",
    hideBelow: 1100,
    render: (m) => <Box component="span" sx={{ color: C.ink2 }}>{m.template_name || "No template"}</Box>,
  },
  {
    key: "sent",
    header: "Sent",
    card: "subtitle",
    sortValue: (m) => m.sent_at ?? "",
    render: (m) => (
      <Box component="span" className="num" sx={{ whiteSpace: "nowrap", color: C.ink2 }}>
        {formatWhen(m.sent_at)}
      </Box>
    ),
  },
  {
    key: "status",
    header: "Status",
    card: "aside",
    align: "right",
    render: (m) => {
      const meta = statusMeta(m.status);
      return (
        <StatusPill tone={meta.tone} title={meta.help}>
          {meta.label}
        </StatusPill>
      );
    },
  },
];

/** Transactional emails (orders, OTPs, cart reminders, chat alerts), newest first. */
export default function SentEmails({ initialQuery = "" }: { initialQuery?: string }) {
  const { idToken } = useAuth();
  const [period, setPeriod] = React.useState("30");
  const [customStart, setCustomStart] = React.useState(isoDay(daysAgo(29)));
  const [customEnd, setCustomEnd] = React.useState(isoDay(new Date()));
  const [templateId, setTemplateId] = React.useState("all");
  const [status, setStatus] = React.useState("all");
  const [query, setQuery] = React.useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = React.useState(initialQuery.trim());
  const [shown, setShown] = React.useState(PAGE_SIZE);
  const [selected, setSelected] = React.useState<SentEmail | null>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const [start, end] = period === "custom" ? [customStart, customEnd] : [isoDay(daysAgo(Number(period) - 1)), isoDay(new Date())];

  const resource = useResource(
    async () =>
      (
        await getSentEmails(idToken as string, {
          start,
          end,
          template_id: templateId === "all" ? undefined : Number(templateId),
          status: status === "all" ? undefined : status,
          q: debouncedQuery || undefined,
        })
      ).data,
    [idToken, start, end, templateId, status, debouncedQuery],
    { enabled: !!idToken && !!start && !!end, errorText: "Couldn't load emails from Brevo." }
  );
  const { data, error, reload } = resource;
  const loading = resource.status === "loading" || resource.status === "idle";

  React.useEffect(() => {
    setShown(PAGE_SIZE);
  }, [data]);

  const summary = data?.summary;
  const messages = data?.messages ?? [];
  const toggleStatus = (value: string) => setStatus((s) => (s === value ? "all" : value));

  return (
    <>
      <KpiStrip max={5}>
        <KpiCard title="Sent" value={summary ? String(summary.sent) : "–"} active={status === "all"} onClick={() => setStatus("all")} />
        <KpiCard title="Delivered" value={summary ? percent(summary.delivered, summary.sent) : "–"} sub={summary ? `${summary.delivered} emails` : undefined} />
        <KpiCard
          title="Opened"
          value={summary ? percent(summary.opened, summary.delivered) : "–"}
          sub={summary ? `${summary.opened} of delivered` : undefined}
          help="Includes Apple Mail privacy-proxy loads, which happen without the person reading."
          active={status === "opened"}
          onClick={() => toggleStatus("opened")}
        />
        <KpiCard title="Clicked" value={summary ? String(summary.clicked) : "–"} active={status === "clicked"} onClick={() => toggleStatus("clicked")} />
        <KpiCard
          title="Problems"
          value={summary ? String(summary.problems) : "–"}
          sub="Bounced, blocked, spam"
          tone={summary && summary.problems > 0 ? "warning" : "default"}
          active={status === "problems"}
          onClick={() => toggleStatus("problems")}
        />
      </KpiStrip>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <TextField select size="small" label="When" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ flex: { xs: "1 1 100%", sm: "0 0 160px" } }}>
          {PERIODS.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        {period === "custom" && (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: { xs: "1 1 calc(50% - 4px)", sm: "0 0 160px" } }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: { xs: "1 1 calc(50% - 4px)", sm: "0 0 160px" } }}
            />
          </>
        )}
        <TextField select size="small" label="Email type" value={templateId} onChange={(e) => setTemplateId(e.target.value)} sx={{ flex: { xs: "1 1 100%", sm: "0 0 210px" } }}>
          <MenuItem value="all">All emails</MenuItem>
          {(data?.templates ?? []).map((t) => (
            <MenuItem key={t.id} value={String(t.id)}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ flex: { xs: "1 1 100%", sm: "0 0 200px" } }}>
          {STATUS_FILTERS.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          placeholder="Search recipient or subject"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          slotProps={{
            htmlInput: { "aria-label": "Search recipient or subject" },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: { xs: "1 1 100%", sm: "1 1 220px" } }}
        />
      </Box>

      {period === "custom" && (
        <Typography variant="caption" sx={{ mt: -1, color: C.muted }}>
          Brevo returns up to 90 days at a time; history goes back to early 2025.
        </Typography>
      )}

      {error && isIpBlock(error) && <BrevoErrorAlert message={error} onRetry={reload} />}
      {error && !isIpBlock(error) && messages.length > 0 && (
        <Banner
          tone="danger"
          action={
            <Button variant="outlined" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error}
        </Banner>
      )}
      {data?.truncated && <Banner tone="warning">There are more emails in this range than one load can pull. Pick a shorter range to see them all.</Banner>}

      <Box sx={{ opacity: loading && data ? 0.6 : 1, transition: "opacity 120ms", minWidth: 0 }}>
        <DataTable
          aria-label="Sent emails"
          rows={error && isIpBlock(error) ? [] : messages.slice(0, shown)}
          columns={columns}
          getRowId={emailId}
          loading={loading && !data}
          error={error && !isIpBlock(error) ? error : null}
          onRetry={reload}
          empty={error ? "Nothing to show." : "No emails match these filters."}
          onRowClick={setSelected}
          selectedId={selected ? emailId(selected) : null}
          footer={
            messages.length > shown ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
                <Button variant="outlined" onClick={() => setShown((n) => n + PAGE_SIZE)} sx={{ minHeight: 44 }}>
                  Show more ({messages.length - shown} left)
                </Button>
              </Box>
            ) : undefined
          }
        />
      </Box>

      <SentEmailDialog email={selected} onClose={() => setSelected(null)} />
    </>
  );
}
